import React from "react";
import { Icon, Button, ButtonProps, useDisclosure } from "@chakra-ui/react";
import { XSquare } from "react-feather";
import { useTranslation } from "next-i18next";

import { AccreditationTypes } from "@/types/accreditation";
import WithdrawAccreditationModal from "@/components/Accreditation/WithdrawAccreditationModal";
import { colorSchemes } from "@/utils/colorSchemes";
import { useUser } from "@/hooks/useUser";

type Props = {
  status: AccreditationTypes;
  accreditationId: number;
  eventId: number;
  userId?: number;
  size?: "xs" | "sm" | "md" | "lg";
  variant?: "solid" | "outline" | "ghost";
  showIcon?: boolean;
  ml?: number | string | Record<string, unknown>;
  mutateList?: boolean;
};

const WithdrawAccreditationButton = ({
  status,
  accreditationId,
  eventId,
  userId,
  size = "sm",
  variant = "solid",
  showIcon = true,
  ml = 0,
  mutateList = false,
  ...props
}: Props &
  Omit<ButtonProps, "size" | "variant">): React.ReactElement | null => {
  const { t } = useTranslation("common");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [session] = useUser();
  const withdrawButtonColors = colorSchemes("red");

  const canWithdraw =
    status === AccreditationTypes.PENDING ||
    status === AccreditationTypes.GRANTED;

  const isUserAccreditation = userId ? userId === session?.user?.id : true;

  if (!canWithdraw || !isUserAccreditation) {
    return null;
  }

  return (
    <>
      <Button
        onClick={onOpen}
        size={size}
        variant={variant}
        ml={ml}
        {...withdrawButtonColors}
        {...props}
      >
        {t("events.withdrawApplication")}
        {showIcon && <Icon as={XSquare} w={4} h={4} ml={2} />}
      </Button>

      {isOpen && (
        <WithdrawAccreditationModal
          isOpen={isOpen}
          onClose={onClose}
          accreditationId={accreditationId}
          eventId={eventId}
          mutateList={mutateList}
        />
      )}
    </>
  );
};

export default WithdrawAccreditationButton;
